import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import type { OptionType } from '@/api/common';
import type { UseInfinitePickerParams } from '@/components/Form/pickers/hooks';
import {
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxValue,
  DebouncedCombobox,
  type DebouncedComboboxProps,
  InfiniteComboboxList,
} from '@/components/Form/pickers/primitives/combobox';
import ComboboxStatus from '@/components/Form/pickers/primitives/combobox/ComboboxStatus';

export type InfiniteComboboxPickerMultipleProps<T extends OptionType> = Omit<
  DebouncedComboboxProps<T, true>,
  | 'items'
  | 'value'
  | 'onValueChange'
  | 'itemToStringValue'
  | 'itemToStringLabel'
  | 'isItemEqualToValue'
> &
  UseInfinitePickerParams & {
    filteredItems?: T[];
    value?: T[];
    className?: string;
    disabled?: boolean;
    isLoading?: boolean;
    error?: Error | null;
    placeholder?: string;
    loadingPlaceholder?: string;
    notFoundContent?: ReactNode;
    renderOption?: (option: T) => ReactNode;
  };

const InfiniteComboboxPickerMultiple = <T extends OptionType>({
  filteredItems,
  value,
  className,
  disabled = false,
  isLoading = false,
  isFetchingNextPage = false,
  error,
  placeholder = t`Select...`,
  loadingPlaceholder = t`Loading...`,
  notFoundContent,
  renderOption,
  fetchNextPage,
  loadMore,
  hasMore,
  loadingOffsetPx,
  'aria-invalid': ariaInvalid,
  ...rest
}: InfiniteComboboxPickerMultipleProps<T>) => {
  return (
    <DebouncedCombobox
      {...rest}
      multiple
      filteredItems={filteredItems}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoading || isFetchingNextPage}
      error={error}
      disabled={disabled}
      value={value ?? []}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {(value ?? []).map((item) => (
            <ComboboxChip key={item.id}>{item.name}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput placeholder={isLoading ? loadingPlaceholder : placeholder} />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>{notFoundContent ?? <Trans>No items were found...</Trans>}</ComboboxEmpty>
        <InfiniteComboboxList
          fetchNextPage={fetchNextPage}
          loadMore={loadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          loadingOffsetPx={loadingOffsetPx}
        >
          {(item: T) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {renderOption ? renderOption(item) : item.name}
            </ComboboxItem>
          )}
        </InfiniteComboboxList>
      </ComboboxContent>
    </DebouncedCombobox>
  );
};

export default InfiniteComboboxPickerMultiple;
